"use client";

/**
 * useHyperliquidRealtimePrice — live HL outcome mid prices over Socket.IO.
 *
 * Mirrors usePredictRealtimePrice. The backend subscribes once per pod to
 * HL's allMids WS feed (see services/hyperliquid/prices-ws.ts) and emits
 * `hl:price:update` events on `hl-market:<assetId>` channels. We just
 * (a) join the channels for the asset_ids on screen, (b) collect ticks
 * into a Map keyed by asset_id.
 *
 * Reuses the singleton socket from usePriceStream — no extra connections.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import {
  getSharedPriceSocket,
  getOrCreateSharedSocket,
  acquireSharedSocket,
  releaseSharedSocket,
} from "./usePriceStream";

export interface HyperliquidPriceTick {
  assetId: number;
  mid: number;
  timestamp: number;
}

interface UseHyperliquidRealtimePriceOptions {
  assetIds: Array<number | string>;
  enabled?: boolean;
}

interface UseHyperliquidRealtimePriceReturn {
  prices: Map<number, HyperliquidPriceTick>;
  isConnected: boolean;
  getPrice: (assetId: number | string) => HyperliquidPriceTick | undefined;
}

export function useHyperliquidRealtimePrice(
  options: UseHyperliquidRealtimePriceOptions,
): UseHyperliquidRealtimePriceReturn {
  const { assetIds, enabled = true } = options;
  const [prices, setPrices] = useState<Map<number, HyperliquidPriceTick>>(new Map());
  const [isConnected, setIsConnected] = useState(false);
  const subscribedRef = useRef<Set<string>>(new Set());

  // Stable join key — sort to make order-independent so React doesn't
  // re-fire the effect when callers pass [yes, no] vs [no, yes].
  const normalizedIds = (assetIds || [])
    .map((a) => Number(a))
    .filter((a) => Number.isFinite(a) && a > 0);
  const joinKey = [...normalizedIds].sort((a, b) => a - b).join(",");

  useEffect(() => {
    if (!enabled || normalizedIds.length === 0) return;

    let isMounted = true;
    let socket: ReturnType<typeof getSharedPriceSocket> = null;

    const handleConnect = () => setIsConnected(true);
    const handleDisconnect = () => setIsConnected(false);
    const handlePriceUpdate = (tick: HyperliquidPriceTick) => {
      if (typeof tick?.assetId !== "number" || typeof tick?.mid !== "number") return;
      setPrices((prev) => {
        const next = new Map(prev);
        next.set(Number(tick.assetId), tick);
        return next;
      });
    };

    const init = async () => {
      socket = getSharedPriceSocket() || (await getOrCreateSharedSocket());
      if (!socket || !isMounted) return;

      acquireSharedSocket();
      socket.on("connect", handleConnect);
      socket.on("disconnect", handleDisconnect);
      if (socket.connected) setIsConnected(true);

      const channels = normalizedIds.map((id) => `hl-market:${id}`);
      socket.emit("subscribe", channels);
      subscribedRef.current = new Set(channels);

      socket.on("hl:price:update", handlePriceUpdate);
    };

    init();

    return () => {
      isMounted = false;
      if (socket) {
        if (subscribedRef.current.size > 0) {
          socket.emit("unsubscribe", [...subscribedRef.current]);
          subscribedRef.current.clear();
        }
        socket.off("connect", handleConnect);
        socket.off("disconnect", handleDisconnect);
        socket.off("hl:price:update", handlePriceUpdate);
        releaseSharedSocket();
      }
    };
  }, [enabled, joinKey]);

  const getPrice = useCallback(
    (assetId: number | string) => prices.get(Number(assetId)),
    [prices],
  );

  return { prices, isConnected, getPrice };
}
